import React, { useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import PtaeCascadePicker, {
  usePtaeOptions, ptaeIndexOf, ptaeText, PtaeCodeLabel,
} from './PtaeCascadePicker.mjs';

/**
 * Fund a claim line by line, by dropping line items into PTAE "buckets".
 *
 * Why buckets rather than a PTAE field on each row: an admin funding a 20-day
 * trip from two awards wants to say "these twelve days come from this code" once,
 * not pick the same code twenty times. Each bucket's amount is the SUM of what
 * is in it, so the allocation balances the claim total by construction.
 *
 * A line can also be SPLIT: one receipt paid partly from one award and partly
 * from another. Split -> choose a PTAE for each part -> say how much each pays
 * -> Assign. Any PTAE can be chosen there, not only an existing bucket - a
 * bucket is made for it. The line then leaves the pool and moves to the right:
 * listed as one item under "Split line items", and as its part in each bucket so
 * the bucket totals add up. When the line is later edited the server keeps the
 * proportions (60 / 40 of a $100 receipt corrected to $120 becomes 72 / 48).
 *
 * Three steps: assign, preview, commit. Assigned lines LEAVE the pool, so what
 * remains on the left is exactly what still needs funding - the count of which
 * is the only thing standing between the admin and being done.
 */

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** A split row whose PTAE is picked fresh rather than from an existing bucket. */
const NEW_PTAE = '__new';

/**
 * Buckets made by a split, and left empty by undoing it, go away again - the
 * admin never asked for them as buckets. One they have dropped lines into is
 * theirs (see dropSelectedInto) and stays.
 */
const tidy = (list) => list.filter(b => !b.autoCreated || b.lineIds.length > 0 || b.parts.length > 0);

/** Distinct left borders so buckets are tellable apart at a glance. */
const BUCKET_TONES = [
  'border-l-indigo-500 bg-indigo-50',
  'border-l-emerald-500 bg-emerald-50',
  'border-l-amber-500 bg-amber-50',
  'border-l-sky-500 bg-sky-50',
  'border-l-rose-500 bg-rose-50',
  'border-l-violet-500 bg-violet-50',
];

/** A fresh copy, so edits never reach the array the screen was opened with. */
const cloneBuckets = (list) => list.map(b => ({
  ...b,
  lineIds: [...(b.lineIds || [])],
  parts: (b.parts || []).map(p => ({ ...p })),
}));

const PtaeBucketAssigner = ({
  claimType, claimTotal, lines, initialBuckets = [], onCancel, onSaved, save,
}) => {
  const { options: ptaeOptions } = usePtaeOptions();
  // Every bucket shows its code WITH its description, as the picker does.
  const ptaeIndex = useMemo(() => ptaeIndexOf(ptaeOptions), [ptaeOptions]);

  // { key, ptae, lineIds: [], parts: [{ lineId, amount }] } - key is local only.
  // lineIds are whole lines; parts are this bucket's share of a split line.
  const seed = () => initialBuckets.map((b, i) => ({
    key: `b${i}`,
    ptae: b.ptae || '',
    lineIds: [...(b.lineIds || [])],
    parts: (b.parts || []).map(p => ({ lineId: p.lineId, amount: round2(p.amount) })),
  }));
  const [buckets, setBuckets] = useState(seed);
  // Monotonic counter for bucket keys. Date.now() would hand two buckets added
  // in the same millisecond the same key, and every key-matched operation uses
  // `map`, so BOTH would receive the dropped lines - putting one line in two
  // buckets and double-counting its amount, silently.
  const nextKey = useRef(initialBuckets.length);
  // What was on the claim when this opened, so the whole session can be thrown
  // away in one click.
  const openedWith = useRef(seed());
  const [selected, setSelected] = useState(() => new Set());
  const [newPtae, setNewPtae] = useState('');
  const [step, setStep] = useState('assign');   // 'assign' | 'preview'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Confirmation after a split is assigned - where the line went.
  const [flash, setFlash] = useState(null);
  // The split being edited: { lineId, rows: [{ bucketKey, amount }] } or null.
  const [splitting, setSplitting] = useState(null);

  const lineById = useMemo(
    () => new Map(lines.map(l => [String(l.lineId), l])),
    [lines]
  );

  const assignedIds = useMemo(() => {
    const set = new Set();
    for (const b of buckets) {
      for (const id of b.lineIds) set.add(String(id));
      for (const p of b.parts) set.add(String(p.lineId));
    }
    return set;
  }, [buckets]);

  /** lineId -> [{ bucketKey, ptae, amount }] for every split line. */
  const splitsByLine = useMemo(() => {
    const map = new Map();
    for (const b of buckets) {
      for (const p of b.parts) {
        const key = String(p.lineId);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ bucketKey: b.key, ptae: b.ptae, amount: p.amount });
      }
    }
    return map;
  }, [buckets]);

  const pool = useMemo(
    () => lines.filter(l => !assignedIds.has(String(l.lineId))),
    [lines, assignedIds]
  );

  const bucketTotal = useCallback(
    (b) => b.lineIds.reduce((sum, id) => sum + (lineById.get(String(id))?.amount || 0), 0)
      + b.parts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [lineById]
  );

  const allocated = buckets.reduce((sum, b) => sum + bucketTotal(b), 0);
  const claimAmount = parseFloat(claimTotal) || 0;
  const unassignedValue = pool.reduce((sum, l) => sum + (l.amount || 0), 0);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    const key = String(id);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const addBucket = () => {
    if (!newPtae) return;
    if (buckets.some(b => b.ptae === newPtae)) {
      setError('That PTAE already has a bucket - add the lines to it instead.');
      return;
    }
    setBuckets(prev => [...prev, { key: `b${nextKey.current++}`, ptae: newPtae, lineIds: [], parts: [] }]);
    setNewPtae('');
    setError(null);
  };

  const dropSelectedInto = (key) => {
    if (selected.size === 0) return;
    // Dropping lines in makes a split-made bucket the admin's own.
    setBuckets(prev => prev.map(b => (
      b.key === key ? { ...b, lineIds: [...b.lineIds, ...selected], autoCreated: false } : b
    )));
    setSelected(new Set());
    setError(null);
    setFlash(null);
  };

  /**
   * Put a line back in the pool. For a split line that means ALL its parts -
   * one part on its own would no longer add up to the line.
   */
  const releaseLine = (lineId) => {
    const key = String(lineId);
    setBuckets(prev => tidy(prev.map(b => ({
      ...b,
      lineIds: b.lineIds.filter(id => String(id) !== key),
      parts: b.parts.filter(p => String(p.lineId) !== key),
    }))));
    setFlash(null);
  };

  /**
   * Remove a bucket. A line split with it goes back to the pool whole: its
   * remaining parts would no longer add up to the line, and guessing where the
   * missing share should go is not ours to do.
   */
  const removeBucket = (key) => {
    const doomed = buckets.find(b => b.key === key);
    const splitLines = new Set((doomed?.parts || []).map(p => String(p.lineId)));
    setBuckets(prev => tidy(prev
      .filter(b => b.key !== key)
      .map(b => ({ ...b, parts: b.parts.filter(p => !splitLines.has(String(p.lineId))) }))));
    setFlash(null);
    setNotice(splitLines.size
      ? `${splitLines.size} split line${splitLines.size === 1 ? '' : 's'} went back to the unassigned list - `
        + 'split them again across the PTAEs that remain.'
      : null);
  };

  /** Empty every bucket back into the pool, keeping the codes. */
  const releaseAll = () => {
    // Keeps the admin's own buckets; ones a split made for itself go.
    setBuckets(prev => tidy(prev.map(b => ({ ...b, lineIds: [], parts: [] }))));
    setFlash(null);
    setSelected(new Set());
    setError(null);
    setNotice(null);
  };

  /** Throw the whole working set away and start from an empty sheet. */
  const removeAllBuckets = () => {
    setBuckets([]);
    setSelected(new Set());
    setError(null);
    setNotice(null);
  };

  /** Back to the allocation as it was when this screen opened. */
  const revert = () => {
    setBuckets(cloneBuckets(openedWith.current));
    setSelected(new Set());
    setError(null);
    setNotice(null);
    setSplitting(null);
    setStep('assign');
  };

  // ------------------------------------------------------------ splitting
  /**
   * Open the split box for one line: its current parts if it is already split,
   * otherwise two empty parts. No buckets are needed first - a part can name
   * any PTAE, and a bucket is made for it on Assign. This used to be greyed
   * out until two buckets existed, and the box could only choose between them.
   */
  const startSplit = (lineId) => {
    const line = lineById.get(String(lineId));
    if (!line) return;
    const existing = splitsByLine.get(String(lineId));
    const rows = existing?.length
      ? existing.map(p => ({ bucketKey: p.bucketKey, newPtae: '', amount: String(p.amount) }))
      // Empty amounts, not a 50/50 guess: a pre-filled split looked finished
      // before the admin had decided anything.
      // A line already sitting whole in a bucket starts from that bucket.
      : (() => {
        const home = buckets.find(b => b.lineIds.some(id => String(id) === String(lineId)));
        const order = home ? [home, ...buckets.filter(b => b.key !== home.key)] : buckets;
        return [0, 1].map(i => ({ bucketKey: order[i]?.key || NEW_PTAE, newPtae: '', amount: '' }));
      })();
    setSplitting({ lineId: String(lineId), rows });
    setError(null);
    setFlash(null);
  };

  const splitLine = splitting ? lineById.get(splitting.lineId) : null;
  const splitRows = splitting?.rows || [];

  /** The PTAE code a split row names - an existing bucket's, or one picked fresh. */
  const rowCode = (r) => (r.bucketKey === NEW_PTAE
    ? (r.newPtae || '')
    : (buckets.find(b => b.key === r.bucketKey)?.ptae || ''));

  const splitSum = round2(splitRows.reduce((t, r) => t + (parseFloat(r.amount) || 0), 0));
  const splitRemaining = splitLine ? round2((splitLine.amount || 0) - splitSum) : 0;
  const splitCodes = splitRows.map(rowCode);
  const splitProblem = !splitting ? null
    : splitRows.length < 2 ? 'A split needs at least two PTAEs'
      : splitCodes.some(c => !c) ? 'Choose a PTAE for every part'
        : new Set(splitCodes).size !== splitCodes.length ? 'Each part must go to a different PTAE'
          : splitRows.some(r => !(parseFloat(r.amount) > 0)) ? 'Enter an amount above zero for every part'
            : Math.abs(splitRemaining) > 0.005
              ? `The parts must add up to ${money(splitLine?.amount)} - ${money(Math.abs(splitRemaining))} ${splitRemaining > 0 ? 'still to place' : 'too much'}`
              : null;

  const setSplitRow = (i, patch) => setSplitting(prev => ({
    ...prev, rows: prev.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)),
  }));

  /** Another part: the next bucket not already used, or a fresh PTAE. */
  const addSplitRow = () => setSplitting(prev => {
    const used = new Set(prev.rows.map(r => r.bucketKey));
    const free = buckets.find(b => !used.has(b.key));
    return { ...prev, rows: [...prev.rows, { bucketKey: free?.key || NEW_PTAE, newPtae: '', amount: '' }] };
  });

  const removeSplitRow = (i) => setSplitting(prev => ({
    ...prev, rows: prev.rows.filter((_r, j) => j !== i),
  }));

  /** Put whatever is left of the line onto one part. */
  const fillRemainder = (i) => setSplitRow(i, {
    amount: String(round2((parseFloat(splitRows[i]?.amount) || 0) + splitRemaining)),
  });

  /** Equal parts, in whole cents, the odd cent on the last. */
  const splitEvenly = () => setSplitting(prev => {
    const n = prev.rows.length;
    const cents = Math.round((splitLine?.amount || 0) * 100);
    const each = Math.floor(cents / n);
    return {
      ...prev,
      rows: prev.rows.map((r, i) => ({
        ...r, amount: String((i === n - 1 ? cents - each * (n - 1) : each) / 100),
      })),
    };
  });

  /**
   * Assign the split: the line leaves the pool and each part lands in its
   * PTAE's bucket, a new bucket being made for a PTAE that has none.
   */
  const assignSplit = () => {
    if (!splitting || splitProblem) return;
    const key = splitting.lineId;

    const created = [];
    const targets = splitRows.map(r => {
      const code = rowCode(r);
      const amount = round2(parseFloat(r.amount));
      const existing = buckets.find(b => b.ptae === code);
      if (existing) return { bucketKey: existing.key, amount };
      const made = { key: `b${nextKey.current++}`, ptae: code, lineIds: [], parts: [], autoCreated: true };
      created.push(made);
      return { bucketKey: made.key, amount };
    });
    const amounts = new Map(targets.map(t => [t.bucketKey, t.amount]));

    setBuckets(prev => tidy([...prev, ...created].map(b => {
      const cleaned = {
        ...b,
        lineIds: b.lineIds.filter(id => String(id) !== key),
        parts: b.parts.filter(p => String(p.lineId) !== key),
      };
      return amounts.has(b.key)
        ? { ...cleaned, parts: [...cleaned.parts, { lineId: key, amount: amounts.get(b.key) }] }
        : cleaned;
    })));
    setSelected(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setFlash(`"${splitLine?.label}" is split across ${targets.length} PTAEs`
      + (created.length ? ` (${created.length} new bucket${created.length === 1 ? '' : 's'} made)` : '')
      + ' - it is now on the right under Split line items.');
    setSplitting(null);
    setNotice(null);
  };

  // ------------------------------------------------------------ commit
  const commit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await save({
        buckets: buckets.map(b => ({
          ptae: b.ptae,
          lineIds: b.lineIds,
          lines: b.parts.map(p => ({ lineId: p.lineId, amount: round2(p.amount) })),
        })),
      });
      onSaved(result);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Compared by code and membership, not by key: reverting rebuilds the array,
  // and a key difference is not a difference the admin made.
  const shapeOf = (list) => JSON.stringify(
    list.map(b => [
      b.ptae || '',
      [...b.lineIds].map(String).sort(),
      b.parts.map(p => `${p.lineId}:${round2(p.amount)}`).sort(),
    ])
  );
  const changed = shapeOf(buckets) !== shapeOf(openedWith.current);

  const emptyBucket = buckets.find(b => b.lineIds.length === 0 && b.parts.length === 0);
  const blocked =
    buckets.length === 0 ? 'Create at least one PTAE bucket'
      : emptyBucket ? `The bucket for ${emptyBucket.ptae} has no line items`
        : pool.length > 0 ? `${pool.length} line item${pool.length === 1 ? '' : 's'} still unassigned`
          : null;

  const lineCount = (b) => b.lineIds.length + b.parts.length;

  /** One row in a bucket - a whole line, or this bucket's part of a split one. */
  const renderBucketLine = (id, part) => {
    const line = lineById.get(String(id));
    if (!line) return null;
    return (
      // Phones: the label gets its own line (it was cut to a few characters,
      // and the "split · part of" note pushed off the edge), with the amount
      // and the touch-sized controls on a row under it.
      <li
        key={`${id}-${part ? 'part' : 'whole'}`}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-2 text-xs bg-white/70 rounded px-2 pt-1.5 pb-0 sm:py-1"
      >
        <span className="min-w-0 break-words sm:truncate text-gray-700">
          {line.label}
          {part && (
            <span className="ml-1.5 text-[10px] uppercase tracking-wide font-semibold text-indigo-700">
              split · part of {money(line.amount)}
            </span>
          )}
        </span>
        <span className="flex items-center justify-end sm:justify-start gap-1 sm:gap-2 flex-shrink-0">
          <span className="tabular-nums text-gray-800 mr-auto sm:mr-0">{money(part ? part.amount : line.amount)}</span>
          <button
            onClick={() => startSplit(id)}
            title={part ? 'Change how this line is split' : 'Pay this line from two or more PTAEs'}
            className="min-h-[44px] sm:min-h-0 px-2 sm:px-0 text-indigo-600 hover:underline"
          >
            {part ? 'Edit split' : 'Split'}
          </button>
          <button
            onClick={() => releaseLine(id)}
            title={part
              ? 'Undo the split - the whole line goes back to the unassigned list'
              : 'Put this line back in the unassigned list'}
            aria-label={part ? undefined : 'Put this line back in the unassigned list'}
            className={`min-h-[44px] sm:min-h-0 ${part
              ? 'px-2 sm:px-0 text-red-700 hover:underline'
              : 'min-w-[44px] sm:min-w-0 text-base sm:text-xs text-gray-400 hover:text-red-600'}`}
          >
            {part ? 'Undo split' : '×'}
          </button>
        </span>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-[9400] bg-gray-100 flex flex-col">
      {/* Header - what is left to do, always visible */}
      <div className="bg-gray-900 text-white px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 sm:gap-4 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="text-base font-bold">
            Assign PTAEs by line item
            <span className="ml-2 text-xs font-normal text-gray-400">
              {claimType === 'perdiem' ? 'Per diem' : 'Expense'} claim
            </span>
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {pool.length === 0
              ? `All ${lines.length} line items assigned · ${money(allocated)} of ${money(claimAmount)}`
              : `${pool.length} of ${lines.length} unassigned · ${money(unassignedValue)} left to fund`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {step === 'preview' && (
            <button
              onClick={() => setStep('assign')}
              className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              ‹ Back to assigning
            </button>
          )}
          <button
            onClick={onCancel}
            disabled={saving}
            className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-xs bg-stanford-red hover:bg-stanford-light-red rounded transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-2.5 flex-shrink-0">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      {flash && !error && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 sm:px-6 py-2.5 flex-shrink-0 flex items-center justify-between gap-3">
          <p className="text-sm text-emerald-900 min-w-0 break-words">{flash}</p>
          <button
            onClick={() => setFlash(null)}
            aria-label="Dismiss"
            className="flex-shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 text-emerald-700 hover:text-emerald-900 text-sm"
          >
            ×
          </button>
        </div>
      )}
      {notice && !error && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5 flex-shrink-0">
          <p className="text-sm text-amber-900">{notice}</p>
        </div>
      )}

      {step === 'assign' ? (
        // Below lg the two panes stack and scroll as ONE page. Each used to keep
        // its own scroller inside the fixed-height screen, which on a phone left
        // the unassigned list a sliver (often zero lines) under the buckets.
        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-visible">
          {/* ---------------- Unassigned pool ---------------- */}
          <div className="lg:w-1/2 flex flex-col flex-shrink-0 lg:flex-shrink min-h-0 border-b lg:border-b-0 lg:border-r border-gray-300 bg-white">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-gray-800">Unassigned line items</h3>
                <p className="text-xs text-gray-500">
                  Tick the lines a PTAE pays for and add them to a bucket. To pay one
                  line from several PTAEs, press Split on it.
                </p>
              </div>
              <button
                onClick={() => setSelected(
                  selected.size === pool.length
                    ? new Set()
                    : new Set(pool.map(l => String(l.lineId)))
                )}
                disabled={pool.length === 0}
                className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
              >
                {selected.size === pool.length && pool.length > 0 ? 'Clear' : 'Select all'}
              </button>
            </div>

            <div className="lg:flex-1 lg:overflow-y-auto p-3 space-y-1.5">
              {pool.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-3xl mb-2">✓</div>
                  <p className="text-gray-700 font-medium">Every line item is assigned</p>
                  <p className="text-sm text-gray-500 mt-1">Review the allocation before saving.</p>
                </div>
              ) : pool.map(line => (
                <div
                  key={line.lineId}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border transition-colors ${
                    selected.has(String(line.lineId))
                      ? 'border-stanford-red bg-red-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <label className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(String(line.lineId))}
                      onChange={() => toggle(line.lineId)}
                      className="mt-0.5 sm:mt-1 w-5 h-5 sm:w-auto sm:h-auto flex-shrink-0"
                    />
                    {/* Wraps on phones: truncated to ~15 characters it was not
                        possible to tell one receipt from the next. */}
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 break-words sm:truncate">{line.label}</span>
                      {line.detail && (
                        <span className="block text-xs text-gray-500 break-words sm:truncate">{line.detail}</span>
                      )}
                    </span>
                    <span className="text-sm font-semibold text-gray-800 tabular-nums whitespace-nowrap">
                      {money(line.amount)}
                    </span>
                  </label>
                  {/* Not inside the label, so clicking it does not tick the line. */}
                  <button
                    onClick={() => startSplit(line.lineId)}
                    disabled={!(line.amount > 0)}
                    title={line.amount > 0 ? 'Pay this line from two or more PTAEs' : 'A line worth nothing cannot be split'}
                    className="min-h-[44px] sm:min-h-0 px-2.5 py-1 text-xs font-medium border border-indigo-300 text-indigo-700 bg-white rounded hover:bg-indigo-50 disabled:opacity-40 whitespace-nowrap"
                  >
                    Split…
                  </button>
                </div>
              ))}
            </div>

            {selected.size > 0 && (
              // Sticky on the one-page phone layout, so the lines just ticked
              // can be dropped without scrolling to the end of a long list.
              <div className="sticky bottom-0 z-10 lg:static lg:z-auto px-4 py-3 border-t border-gray-200 bg-amber-50 flex-shrink-0">
                <p className="text-sm text-amber-900 font-medium mb-2">
                  {selected.size} selected - add to:
                </p>
                <div className="flex flex-wrap gap-2">
                  {buckets.length === 0 ? (
                    <span className="text-sm text-amber-800">
                      Create a bucket on the right first.
                    </span>
                  ) : buckets.map(b => (
                    <button
                      key={b.key}
                      onClick={() => dropSelectedInto(b.key)}
                      className="min-h-[44px] sm:min-h-0 max-w-full text-left px-3 py-1.5 text-xs bg-white border border-amber-300 text-amber-900 rounded-lg hover:bg-amber-100"
                    >
                      + <PtaeCodeLabel code={b.ptae} index={ptaeIndex} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ---------------- Buckets ---------------- */}
          <div className="lg:w-1/2 flex flex-col flex-shrink-0 lg:flex-shrink min-h-0 bg-gray-50">
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="font-semibold text-gray-800">PTAE buckets</h3>
                {/* Reworking an allocation without redoing it row by row. Empty
                    all keeps the codes and hands the lines back; Remove all
                    clears the sheet. */}
                {buckets.length > 0 && (
                  <div className="flex items-center gap-2">
                    {buckets.some(b => lineCount(b) > 0) && (
                      <button
                        onClick={releaseAll}
                        className="min-h-[44px] sm:min-h-0 px-2.5 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
                        title="Send every line item back to the pool, keeping the buckets"
                      >
                        Empty all buckets
                      </button>
                    )}
                    <button
                      onClick={removeAllBuckets}
                      className="min-h-[44px] sm:min-h-0 px-2.5 py-1 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50"
                      title="Delete every bucket and start again"
                    >
                      Remove all
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-full sm:min-w-[280px]">
                  <PtaeCascadePicker
                    value={newPtae}
                    onChange={setNewPtae}
                    options={ptaeOptions}
                  />
                </div>
                <button
                  onClick={addBucket}
                  disabled={!newPtae}
                  className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-40 whitespace-nowrap"
                >
                  + Add bucket
                </button>
              </div>
            </div>

            <div className="lg:flex-1 lg:overflow-y-auto p-3 space-y-3">
              {/* Split lines as whole items - where a line went after Split ->
                  Assign. Each part also appears in its bucket below, which is
                  what the bucket totals are made of. */}
              {splitsByLine.size > 0 && (
                <div className="rounded-lg border border-indigo-200 bg-white p-3">
                  <p className="text-sm font-semibold text-indigo-900">
                    Split line items ({splitsByLine.size})
                  </p>
                  <ul className="mt-2 space-y-2">
                    {[...splitsByLine.entries()].map(([lineId, parts]) => {
                      const line = lineById.get(lineId);
                      if (!line) return null;
                      return (
                        <li key={lineId} className="rounded bg-indigo-50/60 px-2.5 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm text-gray-800 min-w-0 break-words sm:truncate">{line.label}</span>
                            <span className="text-sm font-semibold tabular-nums text-gray-900 whitespace-nowrap">
                              {money(line.amount)}
                            </span>
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {parts.map(pt => (
                              <li key={pt.bucketKey} className="flex items-center justify-between gap-2 text-xs">
                                <PtaeCodeLabel code={pt.ptae} index={ptaeIndex} className="text-gray-700" />
                                <span className="tabular-nums text-gray-800 whitespace-nowrap">{money(pt.amount)}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-1.5 flex gap-3 text-xs">
                            <button onClick={() => startSplit(lineId)} className="min-h-[44px] sm:min-h-0 text-indigo-700 hover:underline">
                              Edit split
                            </button>
                            <button onClick={() => releaseLine(lineId)} className="min-h-[44px] sm:min-h-0 text-red-700 hover:underline">
                              Undo split
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {buckets.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <p className="font-medium text-gray-700">No buckets yet</p>
                  <p className="text-sm mt-1">
                    Pick a PTAE above and add it, then drop line items into it.
                  </p>
                </div>
              ) : buckets.map((b, i) => (
                <div
                  key={b.key}
                  className={`rounded-lg border border-l-4 ${BUCKET_TONES[i % BUCKET_TONES.length]} p-3`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        <PtaeCodeLabel code={b.ptae} index={ptaeIndex} />
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {lineCount(b)} line item{lineCount(b) === 1 ? '' : 's'}
                        {b.parts.length > 0 && ` · ${b.parts.length} split`}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-gray-900 tabular-nums">
                        {money(bucketTotal(b))}
                      </p>
                      <button
                        onClick={() => removeBucket(b.key)}
                        className="min-h-[44px] sm:min-h-0 text-xs text-red-700 hover:underline mt-0.5"
                      >
                        Remove bucket
                      </button>
                    </div>
                  </div>

                  {lineCount(b) > 0 && (
                    <ul className="mt-2 space-y-1">
                      {b.lineIds.map(id => renderBucketLine(id, null))}
                      {b.parts.map(p => renderBucketLine(p.lineId, p))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* ---------------- Preview ---------------- */
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-semibold text-gray-800">Before you assign</h3>
              <p className="text-sm text-gray-600 mt-1">
                Every line item below will be funded from the PTAE it sits under - a
                split line from each PTAE for the part shown. This replaces any funding
                currently on the claim.
              </p>
            </div>

            {buckets.map((b, i) => (
              <div
                key={b.key}
                className={`rounded-xl border border-l-4 ${BUCKET_TONES[i % BUCKET_TONES.length]} shadow-sm overflow-hidden`}
              >
                <div className="px-3 sm:px-4 py-3 flex flex-wrap items-center justify-between gap-2 bg-white/60">
                  <p className="text-sm font-semibold text-gray-900 min-w-0">
                    <PtaeCodeLabel code={b.ptae} index={ptaeIndex} />
                  </p>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">
                    {money(bucketTotal(b))}
                  </p>
                </div>
                <table className="w-full text-sm bg-white">
                  <tbody className="divide-y divide-gray-100">
                    {[...b.lineIds.map(id => ({ id, part: null })), ...b.parts.map(p => ({ id: p.lineId, part: p }))]
                      .map(({ id, part }) => {
                        const line = lineById.get(String(id));
                        if (!line) return null;
                        return (
                          <tr key={`${id}-${part ? 'part' : 'whole'}`}>
                            <td className="px-3 sm:px-4 py-2 text-gray-800">
                              {line.label}
                              {line.detail && (
                                <span className="block text-xs text-gray-500">{line.detail}</span>
                              )}
                              {part && (
                                <span className="block text-xs text-indigo-700">
                                  Split - this PTAE pays {money(part.amount)} of {money(line.amount)}
                                </span>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-right tabular-nums text-gray-800 w-24 sm:w-32 align-top sm:align-middle whitespace-nowrap sm:whitespace-normal">
                              {money(part ? part.amount : line.amount)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ))}

            <div className="bg-white rounded-xl shadow p-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-1 sm:gap-0">
              <span className="font-semibold text-gray-800">Total allocated</span>
              <span className={`text-lg font-bold tabular-nums ${
                Math.abs(allocated - claimAmount) <= 0.01 ? 'text-emerald-700' : 'text-red-700'
              }`}>
                {money(allocated)} of {money(claimAmount)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer - the one action, and why it is unavailable when it is */}
      <div className="ptae-assigner-footer bg-white border-t border-gray-300 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2 sm:gap-3 flex-shrink-0">
        <div className="text-sm">
          <span className={Math.abs(allocated - claimAmount) <= 0.01 ? 'text-emerald-700' : 'text-gray-600'}>
            Allocated <span className="font-semibold tabular-nums">{money(allocated)}</span>
            {' '}of <span className="font-semibold tabular-nums">{money(claimAmount)}</span>
          </span>
          {blocked && <span className="block sm:inline sm:ml-3 text-amber-700">· {blocked}</span>}
        </div>

        {/* Phones: full-width buttons, the forward action (last) on top. */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
          {/* Sits immediately before Preview so the way back is next to the way
              forward. Only offered once something has actually changed. */}
          {changed && (
            <button
              onClick={revert}
              disabled={saving}
              title="Put the buckets back to how they were when this opened"
              className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-40"
            >
              Revert changes
            </button>
          )}
          {step === 'assign' ? (
            <button
              onClick={() => setStep('preview')}
              disabled={!!blocked}
              title={blocked || ''}
              className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-40 font-medium"
            >
              Preview →
            </button>
          ) : (
            <button
              onClick={commit}
              disabled={saving || !!blocked}
              title={blocked || ''}
              className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 font-medium"
            >
              {saving ? 'Assigning…' : '✓ Assign PTAEs'}
            </button>
          )}
        </div>
      </div>

      {/* ---------------- Split box: choose PTAEs, enter amounts, Assign ---------------- */}
      {splitting && splitLine && (
        <div
          className="ptae-split-overlay fixed inset-0 z-[9500] bg-black/50 flex items-center justify-center p-3 sm:p-4"
          onClick={() => setSplitting(null)}
        >
          <div
            // Phones: the whole box scrolls, with the footer pinned. Pinning the
            // tall header as well left the parts a strip under 100px high on a
            // 360x560 screen.
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] flex flex-col overflow-y-auto sm:overflow-visible"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-4 pt-4 sm:px-5 sm:pt-5 pb-3 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Split a line across PTAEs</h3>
              <div className="mt-2 flex items-start justify-between gap-3 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{splitLine.label}</p>
                  {splitLine.detail && <p className="text-xs text-gray-500">{splitLine.detail}</p>}
                </div>
                <p className="text-base font-bold tabular-nums text-gray-900 whitespace-nowrap">
                  {money(splitLine.amount)}
                </p>
              </div>
              <ol className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
                <li><span className="font-semibold text-gray-800">1.</span> Choose a PTAE for each part</li>
                <li><span className="font-semibold text-gray-800">2.</span> Enter how much each pays</li>
                <li><span className="font-semibold text-gray-800">3.</span> Assign - the line moves to the right</li>
              </ol>
            </div>

            <div className="px-4 sm:px-5 py-4 sm:overflow-y-auto sm:min-h-0 space-y-3">
              {splitRows.map((row, i) => (
                <div key={i} className="rounded-lg border border-gray-200 p-3">
                  {/* Phones: "Part n" on its own line, the PTAE full width,
                      then amount | Rest here | remove on one row. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 w-full sm:w-12">Part {i + 1}</span>
                    <select
                      value={row.bucketKey}
                      onChange={e => setSplitRow(i, { bucketKey: e.target.value })}
                      className="flex-1 min-w-full sm:min-w-[220px] min-h-[44px] sm:min-h-0 px-2 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                      aria-label={`PTAE for part ${i + 1}`}
                    >
                      {buckets.map(b => (
                        <option
                          key={b.key}
                          value={b.key}
                          disabled={b.key !== row.bucketKey && splitRows.some(r => r.bucketKey === b.key)}
                        >
                          {ptaeText(b.ptae, ptaeIndex)}
                        </option>
                      ))}
                      <option value={NEW_PTAE}>{buckets.length ? '+ A different PTAE...' : 'Choose a PTAE...'}</option>
                    </select>
                    <div className="relative flex-1 min-w-0 sm:flex-initial">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={row.amount}
                        onChange={e => setSplitRow(i, { amount: e.target.value })}
                        className="w-full sm:w-36 min-h-[44px] sm:min-h-0 pl-5 pr-2 py-2 border border-gray-300 rounded-lg text-sm text-right tabular-nums"
                        aria-label={`Amount part ${i + 1} pays`}
                      />
                    </div>
                    <button
                      onClick={() => fillRemainder(i)}
                      disabled={splitRemaining <= 0.005}
                      title="Put whatever is still to place on this part"
                      className="min-h-[44px] sm:min-h-0 px-3 sm:px-2 py-2 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
                    >
                      Rest here
                    </button>
                    <button
                      onClick={() => removeSplitRow(i)}
                      disabled={splitRows.length <= 2}
                      title="Remove this part"
                      aria-label={`Remove part ${i + 1}`}
                      className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 px-2 py-2 text-gray-400 hover:text-red-600 disabled:opacity-30"
                    >
                      ×
                    </button>
                  </div>
                  {/* Any PTAE, not only an existing bucket - one is made for it on Assign. */}
                  {row.bucketKey === NEW_PTAE && (
                    <div className="mt-2 pl-0 sm:pl-14">
                      <PtaeCascadePicker
                        value={row.newPtae}
                        onChange={code => setSplitRow(i, { newPtae: code })}
                        options={ptaeOptions}
                      />
                    </div>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-0 sm:gap-3">
                <button onClick={addSplitRow} className="min-h-[44px] sm:min-h-0 text-sm text-indigo-700 hover:underline">
                  + Add another part
                </button>
                <button onClick={splitEvenly} className="min-h-[44px] sm:min-h-0 text-sm text-indigo-700 hover:underline">
                  Split evenly
                </button>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white sm:static sm:bg-transparent px-4 sm:px-5 py-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 sm:gap-2 text-sm">
                <span className="text-gray-600">
                  Placed <span className="font-semibold tabular-nums text-gray-900">{money(splitSum)}</span>
                  {' '}of <span className="font-semibold tabular-nums text-gray-900">{money(splitLine.amount)}</span>
                </span>
                <span className={`font-semibold tabular-nums ${
                  Math.abs(splitRemaining) <= 0.005 ? 'text-emerald-700' : splitRemaining > 0 ? 'text-amber-700' : 'text-red-700'
                }`}>
                  {Math.abs(splitRemaining) <= 0.005
                    ? 'Fully placed'
                    : splitRemaining > 0 ? `${money(splitRemaining)} still to place` : `${money(-splitRemaining)} too much`}
                </span>
              </div>
              {splitProblem && (
                <p className="mt-1 text-xs text-amber-800">{splitProblem}</p>
              )}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-3">
                <button
                  onClick={() => setSplitting(null)}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={assignSplit}
                  disabled={!!splitProblem}
                  title={splitProblem || ''}
                  className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 font-medium"
                >
                  Assign split
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PtaeBucketAssigner.propTypes = {
  claimType: PropTypes.string.isRequired,
  claimTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** [{ lineId, lineType, label, detail, date, amount, splitId, allocations }] */
  lines: PropTypes.array.isRequired,
  /** [{ ptae, lineIds, parts: [{ lineId, amount }] }] to reopen an existing allocation. */
  initialBuckets: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  /** ({ buckets }) => Promise<result>, so the caller owns the API call. */
  save: PropTypes.func.isRequired,
};

export default PtaeBucketAssigner;
